import { Factory } from "@/data/dao/Factory";
import { LocationDao } from "@/data/dao/service/LocationDao";
import type { ILocationDetail } from "@/data/model/Location";

import { BaseTabbedController } from "@/portal/controller/BaseTabbedController";
import { ChildLocationsForm } from "@/portal/forms/ChildLocationsForm";
import { LocationAccessEditor } from "@/portal/forms/LocationAccessEditor";
import { LocationApiSettingsForm } from "@/portal/forms/LocationApiSettingsForm";
import { LocationDesignForm } from "@/portal/forms/LocationDesignForm";
import { LocationForm } from "@/portal/forms/LocationForm";
import { LocationGeneralSettingsForm } from "@/portal/forms/LocationGeneralSettingsForm";
import { LocationPrivacySettingsForm } from "@/portal/forms/LocationPrivacySettingsForm";
import { LocationPspForm } from "@/portal/forms/LocationPspForm";
import { PermalinkGenerator } from "@/portal/forms/PermalinkGenerator";
import { TranslationEditor } from "@/portal/forms/TranslationEditor";
import { TwilioConnector } from "@/portal/forms/TwilioConnector";
import { VoucherDesignForm } from "@/portal/forms/VoucherDesignForm";

export class LocationController extends BaseTabbedController<ILocationDetail> {
    private readonly locationForm: LocationForm;
    private designForm?: LocationDesignForm;
    private voucherDesignForm?: VoucherDesignForm;
    private generalSettingsForm?: LocationGeneralSettingsForm;
    private apiSettingsForm?: LocationApiSettingsForm;
    private privacySettingsForm?: LocationPrivacySettingsForm;
    private permalinkGenerator?: PermalinkGenerator;
    private twilioConnector?: TwilioConnector;
    private translationEditor?: TranslationEditor;
    private accessEditor?: LocationAccessEditor;
    private pspForm?: LocationPspForm;
    private childLocations?: ChildLocationsForm;

    constructor(element: JQuery) {
        super(element, Factory.getInstance(LocationDao));

        this.locationForm = new LocationForm(this.formContentByTabName("locationedit"));
        this.registerModelForm(this.locationForm);

        this.onActivateTab = (newTab) => {
            if (newTab === "apisettings") this.showApiSettings();
            if (newTab === "settings") this.showSettings();
            if (newTab === "design") this.showDesign();
            if (newTab === "voucher-design") this.showVoucherDesign();
            if (newTab === "privacy") this.showPrivacySettings();
            else if (newTab === "locationlink") this.showPermaLink();
            else if (newTab === "twilio") this.showTwilioConnector();
            else if (newTab === "translations") this.showTranslations();
            else if (newTab === "access") this.showAccess();
            else if (newTab === "psp") this.showPsp();
            else if (newTab === "child-locations") this.showChildLocations();
        };
    }

    protected onModelLoaded(m?: ILocationDetail) {
        if (m === undefined)
            this.disableTabs([
                "translations",
                "settings",
                "design",
                "apisettings",
                "privacy",
                "locationlink",
                "access",
                "psp",
                "twilio",
                "child-locations",
                "voucher-design",
            ]);
        else
            this.enableTabs([
                "translations",
                "settings",
                "design",
                "apisettings",
                "privacy",
                "locationlink",
                "access",
                "psp",
                "twilio",
                "child-locations",
                "voucher-design",
            ]);

        this.locationForm.setData(m);
    }

    private showSettings() {
        if (this.generalSettingsForm === undefined) {
            this.generalSettingsForm = new LocationGeneralSettingsForm(this.formContentByTabName("settings"));
            this.generalSettingsForm.onSaved = (m) => (this.model = m);
        }

        this.generalSettingsForm.setData(this.model);
    }

    private showDesign() {
        if (this.designForm === undefined) {
            this.designForm = new LocationDesignForm(this.formContentByTabName("design"));
            this.designForm.onSaved = (m) => (this.model = m);
        }

        this.designForm.setData(this.model);
    }

    private showVoucherDesign() {
        if (this.voucherDesignForm === undefined) {
            this.voucherDesignForm = new VoucherDesignForm(this.formContentByTabName("voucher-design"));
            this.voucherDesignForm.onSaved = (m) => (this.model = m);
        }

        this.voucherDesignForm.setData(this.model);
    }

    private showApiSettings() {
        if (this.apiSettingsForm === undefined) {
            this.apiSettingsForm = new LocationApiSettingsForm(this.formContentByTabName("apisettings"));
            this.apiSettingsForm.onSaved = (m) => (this.model = m);
        }

        this.apiSettingsForm.setData(this.model);
    }

    private showPrivacySettings() {
        if (this.privacySettingsForm === undefined) {
            this.privacySettingsForm = new LocationPrivacySettingsForm(this.formContentByTabName("privacy"));
            this.privacySettingsForm.onSaved = (m) => (this.model = m);
        }

        this.privacySettingsForm.setData(this.model);
    }

    private showPermaLink() {
        if (this.permalinkGenerator === undefined)
            this.permalinkGenerator = new PermalinkGenerator(
                this.formContentByTabName("locationlink").find(".permalinkgenerator")
            );

        this.permalinkGenerator.loadForLocation(this.entityId!);
    }

    private showTwilioConnector() {
        if (this.twilioConnector === undefined)
            this.twilioConnector = new TwilioConnector(this.formContentByTabName("twilio").find(".twilioconnector"));

        this.twilioConnector.setByLocation(this.model!);
    }

    private showTranslations() {
        if (this.translationEditor === undefined)
            this.translationEditor = new TranslationEditor(this.formContentByTabName("translations"));

        this.translationEditor.loadForLocation(this.entityId!);
    }

    private showAccess() {
        if (this.accessEditor === undefined)
            this.accessEditor = new LocationAccessEditor(this.formContentByTabName("access"));

        this.accessEditor.loadForLocation(this.entityId!);
    }

    private showPsp() {
        if (this.pspForm === undefined) {
            this.pspForm = new LocationPspForm(this.formContentByTabName("psp"));
        }
        this.pspForm.setData(this.model);
    }

    private showChildLocations() {
        if (this.childLocations == null) {
            this.childLocations = new ChildLocationsForm(this.formContentByTabName("child-locations"));
        }
        this.childLocations.setData(this.model);
    }
}
